/**
 * MattermostConnector — connect to Mattermost via REST API v4.
 *
 * Auth: env:MM_BOT_TOKEN or inline bot token.
 * No npm peer dependencies — uses native fetch + WebSocket (Bun builtins).
 */

import type {
  ConnectContext,
  ConnectorContent,
  ConnectorDeclaration,
  ConnectorFieldSpec,
  ConnectorHandle,
  OperationDescriptor,
  RuntimeSkillDescriptor,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";

interface MattermostState {
  url: string;
  token: string;
  channelFilter: Set<string>;
  botUserId: string;
}

/**
 * Connector for Mattermost — reads and posts messages via the REST API v4.
 * No npm peer dependencies; uses native fetch and Bun WebSocket. Authenticates with a bot token.
 * @docLink packages/factory-assets/concepts#mattermost-connector
 */
export class MattermostConnector extends AbstractConnector {
  readonly name = "mattermost";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      read: (handle, path) => this._read(handle, path),
      write: (handle, path, content) => this._write(handle, path, content),
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
      describeSkill: (handle, declaration) => this._describeSkill(handle, declaration),
    };
  }

  describeFields(): ConnectorFieldSpec[] {
    return [
      {
        key: "url",
        label: "Server URL",
        description: "Mattermost server URL (e.g. https://chat.example.com)",
        type: "url",
        required: true,
        sensitive: false,
        group: "Connection",
        example: "https://chat.example.com",
      },
      {
        key: "token",
        label: "Bot Token",
        description: "Bot user access token from Mattermost",
        type: "api-key",
        required: true,
        sensitive: true,
        group: "Authentication",
      },
      {
        key: "channels",
        label: "Channel Filter",
        description: "Comma-separated channel IDs to restrict operations to.",
        type: "text",
        required: false,
        sensitive: false,
        group: "Options",
      },
    ];
  }

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const url = (ctx.resolvedFields?.url ?? String(declaration.options?.url ?? "")).replace(
      /\/$/,
      "",
    );
    const token = ctx.resolvedFields?.token ?? ctx.secrets?.resolve(declaration.auth ?? "") ?? "";
    const channelsRaw = ctx.resolvedFields?.channels ?? String(declaration.options?.channels ?? "");

    if (!url) throw new Error("MattermostConnector requires a server URL");
    if (!token) throw new Error("MattermostConnector requires a bot token");

    this.log.info("connect start", { url });
    const channelFilter = new Set(
      channelsRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    );
    try {
      const me = await this._apiGet(url, token, "/api/v4/users/me");
      const botUserId = String((me as Record<string, unknown>).id ?? "");
      if (!botUserId) throw new Error("MattermostConnector: failed to authenticate");

      this.log.info("connect ok", { url, botUserId });
      return this.makeHandle(declaration, {
        url,
        token,
        channelFilter,
        botUserId,
      } satisfies MattermostState);
    } catch (err) {
      this.log.error("connect failed", err, { url });
      throw err;
    }
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    // No network teardown required — token is stateless.
    handle.status = "disconnected";
  }

  // ── Tool face methods (private, wired in constructor) ─────────────────────

  private async _read(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null> {
    const state = handle.state as MattermostState;
    try {
      const post = (await this._apiGet(state.url, state.token, `/api/v4/posts/${path}`)) as Record<
        string,
        unknown
      >;
      this._assertChannelAccess(String(post.channel_id ?? ""), state.channelFilter);
      return { data: JSON.stringify(post, null, 2), contentType: "application/json" };
    } catch {
      return null;
    }
  }

  private async _write(
    handle: ConnectorHandle,
    path: string,
    content: ConnectorContent,
  ): Promise<void> {
    if (handle.access === "read-only")
      throw new Error("MattermostConnector: write denied — read-only");
    const state = handle.state as MattermostState;
    const message = typeof content === "string" ? content : String(content.data ?? "");
    await this._apiPut(state.url, state.token, `/api/v4/posts/${path}`, { id: path, message });
    this.emitChange?.({ path, action: "edit", source: "operation" });
  }

  private _describeSkill(
    handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor {
    const state = handle.state as MattermostState;
    const channelCount = state.channelFilter.size;
    const scope = channelCount > 0 ? `${channelCount} channel(s)` : "all channels";
    return {
      id: declaration.id,
      name: `Mattermost — ${scope}`,
      description: `Read and write messages in Mattermost (${scope})`,
      category: "messaging",
      access: declaration.access,
      toolNames:
        declaration.access === "read-write"
          ? [
              "list_channels",
              "get_channel",
              "list_posts",
              "get_thread",
              "create_post",
              "reply_to_thread",
              "update_post",
              "search_posts",
              "get_user",
            ]
          : [
              "list_channels",
              "get_channel",
              "list_posts",
              "get_thread",
              "search_posts",
              "get_user",
            ],
    };
  }

  private _describeOperations(handle: ConnectorHandle): OperationDescriptor[] {
    const ops: OperationDescriptor[] = [
      {
        name: "list_channels",
        description: "List channels the bot has access to",
        accessLevel: "read",
        args: [
          { name: "team_id", type: "string", required: false, description: "Team ID" },
          {
            name: "limit",
            type: "number",
            required: false,
            description: "Max results (default: 50)",
          },
        ],
      },
      {
        name: "get_channel",
        description: "Get channel details by ID",
        accessLevel: "read",
        args: [{ name: "channel_id", type: "string", required: true, description: "Channel ID" }],
      },
      {
        name: "list_posts",
        description: "List recent posts in a channel",
        accessLevel: "read",
        args: [
          { name: "channel_id", type: "string", required: true, description: "Channel ID" },
          {
            name: "limit",
            type: "number",
            required: false,
            description: "Max results (default: 30)",
          },
          {
            name: "before",
            type: "string",
            required: false,
            description: "Post ID to paginate before",
          },
        ],
      },
      {
        name: "get_thread",
        description: "Get all posts in a thread",
        accessLevel: "read",
        args: [{ name: "post_id", type: "string", required: true, description: "Root post ID" }],
      },
      {
        name: "search_posts",
        description: "Search posts across channels",
        accessLevel: "read",
        args: [
          { name: "terms", type: "string", required: true, description: "Search terms" },
          { name: "team_id", type: "string", required: false, description: "Scope to a team" },
        ],
      },
      {
        name: "get_user",
        description: "Get user details by ID",
        accessLevel: "read",
        args: [{ name: "user_id", type: "string", required: true, description: "User ID" }],
      },
    ];

    if (handle.access === "read-write") {
      ops.push(
        {
          name: "create_post",
          description: "Post a new message",
          accessLevel: "write",
          args: [
            { name: "channel_id", type: "string", required: true, description: "Channel ID" },
            { name: "message", type: "string", required: true, description: "Message content" },
          ],
        },
        {
          name: "reply_to_thread",
          description: "Reply to a thread",
          accessLevel: "write",
          args: [
            { name: "channel_id", type: "string", required: true, description: "Channel ID" },
            { name: "root_id", type: "string", required: true, description: "Root post ID" },
            { name: "message", type: "string", required: true, description: "Reply content" },
          ],
        },
        {
          name: "update_post",
          description: "Edit an existing post",
          accessLevel: "write",
          args: [
            { name: "post_id", type: "string", required: true, description: "Post ID" },
            { name: "message", type: "string", required: true, description: "Updated content" },
          ],
        },
      );
    }

    return ops;
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const state = handle.state as MattermostState;

    switch (operation) {
      case "list_channels": {
        const teamId = args.team_id ? String(args.team_id) : undefined;
        const limit = Number(args.limit ?? 50);
        const endpoint = teamId
          ? `/api/v4/users/me/teams/${teamId}/channels?per_page=${limit}`
          : `/api/v4/users/me/channels?per_page=${limit}`;
        const channels = await this._apiGet(state.url, state.token, endpoint);
        return JSON.stringify(
          this._filterChannels(channels as unknown[], state.channelFilter),
          null,
          2,
        );
      }
      case "get_channel": {
        this._assertChannelAccess(String(args.channel_id), state.channelFilter);
        return JSON.stringify(
          await this._apiGet(state.url, state.token, `/api/v4/channels/${args.channel_id}`),
          null,
          2,
        );
      }
      case "list_posts": {
        const cid = String(args.channel_id);
        this._assertChannelAccess(cid, state.channelFilter);
        const limit = Number(args.limit ?? 30);
        const before = args.before ? `&before=${args.before}` : "";
        return JSON.stringify(
          await this._apiGet(
            state.url,
            state.token,
            `/api/v4/channels/${cid}/posts?per_page=${limit}${before}`,
          ),
          null,
          2,
        );
      }
      case "get_thread": {
        const postId = String(args.post_id);
        // Resolve the post's channel first so the thread can't bypass the filter.
        const post = (await this._apiGet(
          state.url,
          state.token,
          `/api/v4/posts/${postId}`,
        )) as Record<string, unknown>;
        this._assertChannelAccess(String(post.channel_id ?? ""), state.channelFilter);
        return JSON.stringify(
          await this._apiGet(state.url, state.token, `/api/v4/posts/${postId}/thread`),
          null,
          2,
        );
      }
      case "search_posts": {
        const body: Record<string, unknown> = { terms: String(args.terms), is_or_search: false };
        if (args.team_id) body.team_id = String(args.team_id);
        return JSON.stringify(
          await this._apiPost(state.url, state.token, "/api/v4/posts/search", body),
          null,
          2,
        );
      }
      case "get_user":
        // Not channel-addressed — no channel to scope against, so the filter
        // does not apply here.
        return JSON.stringify(
          await this._apiGet(state.url, state.token, `/api/v4/users/${args.user_id}`),
          null,
          2,
        );
      case "create_post": {
        this._assertWriteAccess(handle);
        const cid = String(args.channel_id);
        this._assertChannelAccess(cid, state.channelFilter);
        const post = await this._apiPost(state.url, state.token, "/api/v4/posts", {
          channel_id: cid,
          message: String(args.message),
        });
        const postId = String((post as Record<string, unknown>).id ?? "");
        this.emitChange?.({
          path: `channels/${cid}/posts/${postId}`,
          action: "create",
          source: "operation",
        });
        return JSON.stringify(post, null, 2);
      }
      case "reply_to_thread": {
        this._assertWriteAccess(handle);
        const cid = String(args.channel_id);
        this._assertChannelAccess(cid, state.channelFilter);
        const post = await this._apiPost(state.url, state.token, "/api/v4/posts", {
          channel_id: cid,
          root_id: String(args.root_id),
          message: String(args.message),
        });
        const postId = String((post as Record<string, unknown>).id ?? "");
        this.emitChange?.({
          path: `channels/${cid}/posts/${postId}`,
          action: "create",
          source: "operation",
        });
        return JSON.stringify(post, null, 2);
      }
      case "update_post": {
        this._assertWriteAccess(handle);
        const pid = String(args.post_id);
        const updated = await this._apiPut(state.url, state.token, `/api/v4/posts/${pid}`, {
          id: pid,
          message: String(args.message),
        });
        this.emitChange?.({ path: `posts/${pid}`, action: "edit", source: "operation" });
        return JSON.stringify(updated, null, 2);
      }
      default:
        throw new Error(`MattermostConnector has no operation "${operation}"`);
    }
  }

  private _assertWriteAccess(handle: ConnectorHandle): void {
    if (handle.access === "read-only")
      throw new Error("MattermostConnector: operation denied — read-only");
  }

  private _assertChannelAccess(channelId: string, filter: Set<string>): void {
    if (filter.size > 0 && !filter.has(channelId))
      throw new Error(`MattermostConnector: channel "${channelId}" not in filter`);
  }

  private _filterChannels(channels: unknown[], filter: Set<string>): unknown[] {
    if (filter.size === 0) return channels;
    return channels.filter((ch) => {
      const id = (ch as Record<string, unknown>).id;
      return typeof id === "string" && filter.has(id);
    });
  }

  private async _apiGet(baseUrl: string, token: string, path: string): Promise<unknown> {
    const res = await fetch(baseUrl + path, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const body = await res.text();
      this.log.warn("REST error", { method: "GET", endpoint: path, status: res.status });
      throw new Error(`Mattermost GET ${path}: ${res.status} ${body}`);
    }
    return res.json();
  }

  private async _apiPost(
    baseUrl: string,
    token: string,
    path: string,
    body: unknown,
  ): Promise<unknown> {
    const res = await fetch(baseUrl + path, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Mattermost POST ${path}: ${res.status} ${await res.text()}`);
    return res.json();
  }

  private async _apiPut(
    baseUrl: string,
    token: string,
    path: string,
    body: unknown,
  ): Promise<unknown> {
    const res = await fetch(baseUrl + path, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Mattermost PUT ${path}: ${res.status} ${await res.text()}`);
    return res.json();
  }
}

/**
 * Creates a new MattermostConnector instance.
 * @returns Configured MattermostConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#mattermost-connector-factory
 */
export function createConnector(): MattermostConnector {
  return new MattermostConnector();
}
